import { Check, WarningAmber } from "@mui/icons-material"
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material"
import { FC, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  doorDivergenceClassKey,
  IRREVERSIBLE_CLASSES,
} from "shared/saLockerDoorRepairData"
import { sl2ApiUrl } from "src/features/baseQuerySl2"
import {
  useApplySaLockerDoorRepairV1Mutation,
  useFetchSaLockerDoorV1Mutation,
  type SaLockerDoorFetchResult,
} from "src/features/sa-locker-repair/saLockerRepairApi"
import PayloadList from "./PayloadList"
import SaLockerProvenanceLine from "./SaLockerProvenanceLine"
import { doorFetchProvenance } from "./saLockerProvenance"

/**
 * The per-door repair action, for SST **v1** lockers only (LOCKER-343, Deliverable B).
 *
 * ## Two presses, and the second one is not a formality
 *
 * *Fetch from SST v1* is read-only and repeatable. *Apply* is a separate, explicit
 * press, and for class B it needs a THIRD — a confirm — because v1 `CLOSE` is terminal
 * and cannot be walked back. That is not belt-and-braces: the drawer is opened while
 * someone is on the phone with a pharmacy, and the difference between "free the door"
 * and "close a reservation on a parcel that is still in there" is one click.
 *
 * ## Why the fetch button exists at all, rather than fetching on open
 *
 * Because it calls a live partner API from an operator's browser session, per door.
 * Auto-fetching on drawer open would hit SST every time anyone clicked any door while
 * looking around — and the drawer's existing job is to show what we already hold,
 * which needs no partner call. The partner read is a deliberate act.
 *
 * ## Only rendered for v1
 *
 * The V2 drawer already carries the partner's reservations inline on the door
 * (`door.reservations[]`), so it has no hole to fill; none of the six classes is even
 * defined against a V2 payload, and the backend refuses a V2 locker outright.
 */
const RESULT_MAX_HEIGHT = 320

/**
 * ⚠ A VERDICT BELONGS TO THE DOOR IT WAS FETCHED FOR, and this component does not
 * enforce that itself — the caller must give it a `key` that includes the door id, so
 * React remounts it and every piece of local state is discarded on a door change.
 *
 * Carrying a verdict across a door change would offer an Apply computed from a
 * different door, which is the worst bug available in a per-door repair tool. The
 * first version of this file did the reset by comparing props during render; a `key`
 * is the same guarantee with no logic to get wrong, and it also resets the armed
 * confirmation for free. `SaLockerDoorInspector` supplies it.
 */
export const SaLockerDoorRepairPanel: FC<{
  /** The partner's v1 locker id — what the endpoints key on. */
  sstLockerId?: string
  /** The partner's v1 door id. */
  sstDoorId?: string
}> = ({ sstLockerId, sstDoorId }) => {
  const { t } = useTranslation()
  const [fetchDoor, fetchState] = useFetchSaLockerDoorV1Mutation()
  const [applyRepair, applyState] = useApplySaLockerDoorRepairV1Mutation()
  const [confirmingIrreversible, setConfirmingIrreversible] = useState(false)
  const [result, setResult] = useState<SaLockerDoorFetchResult["data"] | null>(null)

  const onFetch = useCallback(async () => {
    if (!sstLockerId || !sstDoorId) return
    setConfirmingIrreversible(false)
    // Discard the previous apply's outcome. Without this a green "applied" banner
    // survived the next Fetch and sat beside a fresh verdict, reading as though the
    // new verdict had already been repaired.
    applyState.reset?.()
    const res = await fetchDoor({ sstLockerId, sstDoorId })
    // `res.data`, not `"data" in res`. RTK's result union declares `data` on BOTH
    // arms (optional-undefined on the error arm), and the `in` operator does not
    // narrow an optional property — so `"data" in res` compiles and then leaves
    // `res.data` possibly undefined. A truthiness check narrows properly.
    if (res.data) setResult(res.data.data)
  }, [fetchDoor, sstLockerId, sstDoorId])

  const divergence = result?.divergence
  const cls = divergence?.class ?? null
  const action = divergence?.action ?? null
  const irreversible = !!cls && IRREVERSIBLE_CLASSES.includes(cls)

  /**
   * Every row of OURS the classifier considered for this door — not just the one that
   * resolved by the partner's id. De-duplicated because `sl2Reservation`, when set, is
   * also in `sl2ReservationsOnDoor`.
   */
  const ourRows = useMemo(() => {
    if (!result) return []
    const onDoor = result.sl2ReservationsOnDoor ?? []
    const resolved = result.sl2Reservation
    if (!resolved) return onDoor
    const resolvedId = (resolved as { id?: string }).id
    return [
      resolved,
      ...onDoor.filter((r) => (r as { id?: string }).id !== resolvedId),
    ]
  }, [result])

  const onApply = useCallback(async () => {
    if (!sstLockerId || !sstDoorId || !cls || !action) return
    const res = await applyRepair({
      sstLockerId,
      sstDoorId,
      divergenceClass: cls,
      action,
    })
    setConfirmingIrreversible(false)
    // The apply returns the door re-read AFTER the change, so the panel shows the
    // repaired state without the operator having to press Fetch again.
    const after = res.data?.data.after
    if (after) setResult(after)
  }, [applyRepair, sstLockerId, sstDoorId, cls, action])

  if (!sstLockerId || !sstDoorId) return null

  const fetchError = fetchState.error as { data?: unknown } | undefined
  const applyResult = applyState.data?.data

  return (
    <Box data-ui="UI-SL2W-148"
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        <Typography variant="subtitle2" fontWeight={600}>
          {t("saLockerConsole.repair.title")}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={onFetch}
          disabled={fetchState.isLoading}
          startIcon={fetchState.isLoading ? <CircularProgress size={14} /> : undefined}
        >
          {t("saLockerConsole.repair.fetch")}
        </Button>
        {result && (
          <Chip
            size="small"
            variant="outlined"
            color={cls ? "warning" : "success"}
            label={
              cls
                ? `${t("saLockerConsole.repair.classChip")} ${cls}`
                : t("saLockerConsole.repair.clean")
            }
          />
        )}
      </Stack>

      <Typography variant="caption" color="text.secondary">
        {t("saLockerConsole.repair.fetchHint")}
      </Typography>

      {/* LOCKER-355 §8 — display only. This panel and the drawer above it read
          DIFFERENT sources, and when they disagree the whole question is which
          endpoint each one hit. Rendered before any press, so the URL is on
          screen whether or not the operator fetches. */}
      <SaLockerProvenanceLine
        descriptor={doorFetchProvenance({ base: sl2ApiUrl, sstLockerId, sstDoorId })}
      />

      {fetchState.isError && (
        <Alert severity="error" variant="outlined" sx={{ fontSize: "0.8rem" }}>
          {String(
            (fetchError?.data as { message?: string })?.message ??
              t("saLockerConsole.repair.fetchFailed"),
          )}
        </Alert>
      )}

      {result && (
        <>
          <Alert
            severity={cls ? "warning" : "success"}
            variant="outlined"
            icon={cls ? <WarningAmber fontSize="inherit" /> : <Check fontSize="inherit" />}
            sx={{ fontSize: "0.8rem" }}
          >
            <AlertTitle sx={{ fontSize: "0.8rem", mb: 0.5 }}>
              {cls
                ? `${t("saLockerConsole.repair.classChip")} ${cls} — ${t(doorDivergenceClassKey(cls))}`
                : t("saLockerConsole.repair.clean")}
            </AlertTitle>
            {/* The backend's own sentence, not a re-derived one: the class and the
                reason must never disagree, and the class was decided there. */}
            {divergence?.reason}
          </Alert>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              columnGap: 3,
              rowGap: 2,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" fontWeight={600} display="block" mb={0.5}>
                {t("saLockerConsole.repair.partnerSide")}
              </Typography>
              <Box sx={{ maxHeight: RESULT_MAX_HEIGHT, overflow: "auto", pr: 1 }}>
                {result.partnerReservation ? (
                  <PayloadList data={result.partnerReservation} />
                ) : (
                  <Typography variant="caption" color="text.disabled">
                    {t("saLockerConsole.repair.partnerNone")}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" fontWeight={600} display="block" mb={0.5}>
                {t("saLockerConsole.repair.ourSide")}
              </Typography>
              <Box sx={{ maxHeight: RESULT_MAX_HEIGHT, overflow: "auto", pr: 1 }}>
                {/* `sl2Reservation` is the row that resolved by the PARTNER's id — and
                    on class C it is `undefined` BY DEFINITION, because class C is
                    exactly "no row answers to the partner's id". Rendering only that
                    showed "we hold nothing" while the reason sentence named a row the
                    apply was about to mutate: the operator approved a write to a row
                    they were never shown. `ourRows` below is what the classifier
                    actually reasoned over. */}
                {ourRows.length > 0 ? (
                  <Stack direction="column" spacing={2}>
                    {ourRows.map((r, i) => (
                      <PayloadList key={(r as { id?: string }).id ?? i} data={r} />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.disabled">
                    {t("saLockerConsole.repair.ourNone")}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          {action && (
            <Stack direction="column" spacing={1}>
              {irreversible && (
                <Alert severity="error" variant="outlined" sx={{ fontSize: "0.8rem" }}>
                  <AlertTitle sx={{ fontSize: "0.8rem", mb: 0.5 }}>
                    {t("saLockerConsole.repair.irreversibleTitle")}
                  </AlertTitle>
                  {t("saLockerConsole.repair.irreversibleBody")}
                </Alert>
              )}
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                {/* NO default, NO pre-tick, and for an irreversible class no direct
                    Apply at all — the button becomes a two-stage arm/confirm. */}
                {irreversible && !confirmingIrreversible ? (
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={() => setConfirmingIrreversible(true)}
                  >
                    {t("saLockerConsole.repair.armIrreversible")}
                  </Button>
                ) : (
                  <Button
                    size="small"
                    color={irreversible ? "error" : "primary"}
                    variant="contained"
                    onClick={onApply}
                    disabled={applyState.isLoading}
                    startIcon={
                      applyState.isLoading ? <CircularProgress size={14} /> : undefined
                    }
                  >
                    {irreversible
                      ? t("saLockerConsole.repair.confirmIrreversible")
                      : t(`saLockerConsole.repair.action.${action}`)}
                  </Button>
                )}
                {irreversible && confirmingIrreversible && (
                  <Button size="small" onClick={() => setConfirmingIrreversible(false)}>
                    {t("cancel")}
                  </Button>
                )}
              </Stack>
            </Stack>
          )}

          {cls === "F" && (
            <Alert severity="info" variant="outlined" sx={{ fontSize: "0.8rem" }}>
              {t("saLockerConsole.repair.reportOnly")}
            </Alert>
          )}
        </>
      )}

      {applyState.isError && (
        <Alert severity="error" variant="outlined" sx={{ fontSize: "0.8rem" }}>
          {t("saLockerConsole.repair.applyFailed")}
        </Alert>
      )}
      {applyResult && !applyResult.applied && (
        // A refusal is a normal, informative outcome — most often "the door is no
        // longer the class you chose", which is the stale-screen guard doing its job.
        <Alert severity="warning" variant="outlined" sx={{ fontSize: "0.8rem" }}>
          {applyResult.reason ?? t("saLockerConsole.repair.applyRefused")}
        </Alert>
      )}
      {applyResult?.applied && (
        // `auditWritten === false` means the change IS live but unrecorded. For a
        // class-B partner CLOSE that is an irreversible write with no trace, so it
        // must not render as an unqualified success.
        <Alert
          severity={applyResult.auditWritten === false ? "warning" : "success"}
          variant="outlined"
          sx={{ fontSize: "0.8rem" }}
        >
          {applyResult.auditWritten === false
            ? t("saLockerConsole.repair.appliedNoAudit")
            : t("saLockerConsole.repair.applied")}
        </Alert>
      )}
    </Box>
  )
}

export default SaLockerDoorRepairPanel
